import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize user state from localStorage and fetch full user details
  useEffect(() => {
    const fetchUserData = async () => {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
      
      if (!token || !userId) {
        setIsLoading(false);
        return;
      }
      
      try {
        // Fetch complete user data from API
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data && response.data.user) {
          setUser({
            ...response.data.user,
            id: userId,
            token: token
          });
        } else {
          // If API call didn't return user data, clear the localStorage and don't set user
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
          setUser(null);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        // If API call fails with an authentication error, clear localStorage
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
          setUser(null);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, []);

  // Login function
  const login = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('userId', userData.id);
    setUser({ ...userData, token });
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    setUser(null);
  };

  // Update user details
  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  // Check if token is valid
  const isValidToken = () => {
    const token = localStorage.getItem('token');
    return !!token && !!user;
  };

  const value = {
    user,
    isAuthenticated: isValidToken(),
    isLoading,
    login,
    logout,
    updateUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook for using the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 